const findKeyCaseInsensitive = (needle: string, map: Record<string, unknown>) => {
  const lower = needle.toLowerCase()
  return Object.keys(map).find(k => k.toLowerCase() === lower)
}

type LabelHit = { start: number; end: number; key: string }

/**
 * If a matched element id is immediately preceded by `-`, the real id is usually the whole
 * token back to the previous whitespace (e.g. `beam-of-<uuid>` vs bare `<uuid>` inside it).
 * Prefer the expanded token when it matches a known key; otherwise drop the inner match.
 */
const resolveDashPrefixedHit = (
  message: string,
  matchStart: number,
  matchEnd: number,
  matchedKey: string,
  keys: readonly string[],
): LabelHit | null => {
  const keyLookup = Object.fromEntries(keys.map(key => [key, true])) as Record<string, unknown>

  if (matchStart > 0 && message[matchStart - 1] === '-') {
    let expandedStart = matchStart
    while (expandedStart > 0 && !/\s/.test(message[expandedStart - 1])) {
      expandedStart -= 1
    }
    const candidate = message.slice(expandedStart, matchEnd)
    const canonical = findKeyCaseInsensitive(candidate, keyLookup)
    if (canonical) {
      return {
        start: expandedStart,
        end: expandedStart + canonical.length,
        key: canonical,
      }
    }
    return null
  }

  return { start: matchStart, end: matchEnd, key: matchedKey }
}

const collectLabelHits = (message: string, keys: readonly string[]): LabelHit[] => {
  const lower = message.toLowerCase()
  const hits: LabelHit[] = []
  for (const k of keys) {
    const kl = k.toLowerCase()
    let from = 0
    while (from < message.length) {
      const idx = lower.indexOf(kl, from)
      if (idx === -1) break
      const resolved = resolveDashPrefixedHit(message, idx, idx + k.length, k, keys)
      if (resolved) hits.push(resolved)
      from = idx + 1
    }
  }
  return hits
}

/** Non-overlapping: at each index, prefer the longest span (handles one key being a substring of another). */
const pickLabelHits = (messageLen: number, hits: LabelHit[]): LabelHit[] => {
  const atStart = new Map<number, LabelHit[]>()
  for (const h of hits) {
    const arr = atStart.get(h.start) ?? []
    arr.push(h)
    atStart.set(h.start, arr)
  }
  const chosen: LabelHit[] = []
  let pos = 0
  while (pos < messageLen) {
    const candidates = atStart.get(pos)
    if (!candidates) {
      pos += 1
      continue
    }
    const best = candidates.reduce((a, b) => (b.end - b.start > a.end - a.start ? b : a))
    chosen.push(best)
    pos = best.end
  }
  return chosen.sort((a, b) => a.start - b.start)
}

export const formatErrorWithGuidLabels = (message: string, guidToLabel: Record<string, string>) => {
  const keys = Object.keys(guidToLabel)
  if (keys.length === 0) return message

  const chosen = pickLabelHits(message.length, collectLabelHits(message, keys))
  let out = ''
  let cursor = 0
  for (const h of chosen) {
    out += message.slice(cursor, h.start)
    const raw = message.slice(h.start, h.end)
    out += `${guidToLabel[h.key]} (${raw})`
    cursor = h.end
  }
  out += message.slice(cursor)
  return out
}

/** Distinct keys from `elementKeys` that appear in `message`, in left-to-right order (same spans as labeling). */
export const findElementGuidsInMessageOrdered = (
  message: string,
  elementKeys: readonly string[],
): string[] => {
  if (elementKeys.length === 0) return []

  const chosen = pickLabelHits(message.length, collectLabelHits(message, elementKeys))
  const seen = new Set<string>()
  const ordered: string[] = []
  for (const h of chosen) {
    if (seen.has(h.key)) continue
    seen.add(h.key)
    ordered.push(h.key)
  }
  return ordered
}

/** Short user-facing line when an element guid is known (full backend text is behind “Details”). Never includes ids. */
export const buildCalculateErrorSummaryMessage = (
  primaryElementGuid: string,
  guidToLabel: Record<string, string>,
) => {
  const labelKey = findKeyCaseInsensitive(primaryElementGuid, guidToLabel)
  const label = labelKey ? guidToLabel[labelKey] : undefined
  if (label) {
    return `Fehler beim Starten der Berechnung bei Element ${label}`
  }
  return 'Fehler beim Starten der Berechnung bei einem Element'
}
